import * as React from "react";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { NumberInput } from "@/components/ui/number-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LucideIcon } from "lucide-react";

interface SliderWithInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  defaultValue?: number;
  icon?: LucideIcon;
  tooltip?: string;
  subline?: string;
  leftLabel?: string;
  rightLabel?: string;
  leftTooltip?: string;
  rightTooltip?: string;
  inputWidth?: number;
  formatValue?: (value: number) => string;
  decimalScale?: number;
}

export function SliderWithInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  defaultValue,
  icon: Icon,
  tooltip,
  subline,
  leftLabel,
  rightLabel,
  leftTooltip,
  rightTooltip,
  inputWidth = 80,
  formatValue,
  decimalScale = 0,
}: SliderWithInputProps) {
  const [localSliderValue, setLocalSliderValue] = React.useState(value);
  const [isDragging, setIsDragging] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    if (!isDragging) {
      setLocalSliderValue(value);
    }
  }, [value, isDragging]);

  const handleSliderChange = (values: number[]) => {
    const newValue = values[0];
    setLocalSliderValue(newValue);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout to update the parent value
    timeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, 50); // Small delay to smooth out rapid changes
  };

  const handleSliderDragStart = () => {
    setIsDragging(true);
  };

  const handleSliderDragEnd = () => {
    setIsDragging(false);
    onChange(localSliderValue);
  };

  const handleValueChange = (newValue: number | undefined) => {
    if (newValue !== undefined) {
      setLocalSliderValue(newValue);
      onChange(newValue);
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const renderTooltipLabel = (label: string, tooltipContent?: string) => {
    if (tooltipContent) {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-muted-foreground cursor-help">
                {label}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">{tooltipContent}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return <span className="text-xs text-muted-foreground">{label}</span>;
  };

  return (
    <div className="w-full space-y-2">
      <div className={`flex justify-between gap-4 ${subline ? 'items-start' : 'items-center'}`}>
        <div className={`flex gap-2 flex-1 ${subline ? 'items-start' : 'items-center'}`}>
          {Icon && <Icon className={`h-4 w-4 text-primary ${subline ? 'mt-0.5' : ''}`} />}
          <div className="flex-1 space-y-0.5">
            <div className="flex items-center gap-2">
              <Label>{label}</Label>
              {tooltip && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4 text-muted-foreground"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <path d="M12 17h.01" />
                      </svg>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {subline && (
              <p className="text-xs text-muted-foreground">
                {subline}
              </p>
            )}
          </div>
        </div>
        <div style={{ width: `${inputWidth}px` }}>
          <NumberInput
            value={localSliderValue}
            onValueChange={handleValueChange}
            min={min}
            max={max}
            step={step}
            defaultValue={defaultValue}
            fixedDecimalScale={true}
            decimalScale={decimalScale}
            className="w-full"
            stepper={step}
            format={formatValue}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Slider
          value={[localSliderValue]}
          onValueChange={handleSliderChange}
          onPointerDown={handleSliderDragStart}
          onPointerUp={handleSliderDragEnd}
          min={min}
          max={max}
          step={step}
          defaultValue={defaultValue ? [defaultValue] : undefined}
          className="cursor-pointer [&_[role=slider]]:cursor-grab [&_[role=slider]:active]:cursor-grabbing"
        />
        {(leftLabel || rightLabel) && (
          <div className="flex justify-between">
            {leftLabel && renderTooltipLabel(leftLabel, leftTooltip)}
            {rightLabel && renderTooltipLabel(rightLabel, rightTooltip)}
          </div>
        )}
      </div>
    </div>
  );
}
